import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

import { BaseModalProvider } from './BaseModalContext';
import styles from './CenteredModal.module.scss';
import CenteredModalHeader from './CenteredModalHeader';
import CenteredModalContent from './CenteredModalContent';

interface CenteredModalProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    className?: string;
    maxWidth?: 'small' | 'medium' | 'large';
}

export const CenteredModal: React.FC<CenteredModalProps> & {
    Header: typeof CenteredModalHeader;
    Content: typeof CenteredModalContent;
} = ({ isOpen, onClose, children, className, maxWidth = 'medium' }) => {
    // Handle ESC key press
    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscKey);
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        // Only close if clicking on the overlay itself (not on children)
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const maxWidthClass = {
        small: styles.maxWidthSmall,
        medium: styles.maxWidthMedium,
        large: styles.maxWidthLarge
    }[maxWidth];

    const modalContent = (
        <div 
            className={styles.modalOverlay} 
            onClick={handleOverlayClick}
        >
            <div 
                className={`${styles.modalContainer} ${maxWidthClass} ${className || ''}`}
            >
                <BaseModalProvider onClose={onClose}>
                    {children}
                </BaseModalProvider>
            </div>
        </div>
    );

    // Render modal in portal to avoid z-index issues
    return createPortal(modalContent, document.body);
};

// Attach sub-components to create composition API
CenteredModal.Header = CenteredModalHeader;
CenteredModal.Content = CenteredModalContent;

export default CenteredModal;
